import React from 'react';
import { formatDate, renderCell, cellText } from '../utils/helpers';
import {
  REPORT_TYPE_LABELS,
  maintenanceTypeLabel,
  maintenanceStatusLabel,
} from '../utils/pdfGeneratorSimple';
import EquipmentDetailSummary from './EquipmentDetailSummary';
import { normalizeEquipmentRecord } from '../utils/equipmentDisplay';
import { normalizeMaintenanceRecord, normalizeReportPayload } from '../utils/reportPayload';

const REPORT_EQUIPMENT_EXCLUDE = ['clientName', 'clientCompany', 'positionX', 'positionY'];

/**
 * On-screen / HTML print layout aligned with generateSimpleReportPDF (jsPDF).
 */
const SimpleReportDocument = ({
  reportData,
  reportType,
  clientName,
  clientCompany,
  generatedAt,
}) => {
  const data = normalizeReportPayload(reportData || {});
  const equipmentList = data.equipment || [];
  const maintenanceList = data.maintenance || [];
  const expiringList = data.expiring || [];
  const showExpiring = reportType === 'expiring' || reportType === 'full';
  const reportDate = generatedAt ? new Date(generatedAt) : new Date();

  const fieldLine = (label, value) => (
    <p className="text-sm text-gray-800 leading-snug pl-4">
      <span className="text-gray-600">{label}: </span>
      {renderCell(value)}
    </p>
  );

  return (
    <article className="simple-report-document bg-white text-gray-900">
      <header className="bg-red-50 px-6 py-6 text-center border-b-2 border-red-600">
        <h1 className="text-2xl font-bold text-red-600">PFE - Gestion Incendie</h1>
        <p className="text-sm text-gray-500 mt-1">Gestion des Équipements de Sécurité Incendie</p>
      </header>

      <div className="px-6 py-6 space-y-6">
        <section>
          <h2 className="text-lg font-bold text-gray-900 mb-3">
            {REPORT_TYPE_LABELS[reportType] || 'Rapport'}
          </h2>
          <div className="text-sm text-gray-600 space-y-1">
            <p>
              <span className="font-medium">Client:</span> {renderCell(clientName)}
            </p>
            {clientCompany && (
              <p>
                <span className="font-medium">Société:</span> {renderCell(clientCompany)}
              </p>
            )}
            <p>
              <span className="font-medium">Date:</span>{' '}
              {formatDate(reportDate, 'dd/MM/yyyy HH:mm')}
            </p>
          </div>
        </section>

        {equipmentList.length > 0 && (
          <section className="break-inside-avoid">
            <h3 className="text-base font-bold text-red-600 mb-3">Équipements</h3>
            <ul className="space-y-4">
              {equipmentList.map((eq, index) => (
                <li key={eq.id ?? index} className="break-inside-avoid border-b border-gray-100 pb-4">
                  <p className="text-sm font-bold text-gray-900 mb-2">
                    {index + 1}. {renderCell(normalizeEquipmentRecord(eq).serialNumber)}
                  </p>
                  <EquipmentDetailSummary
                    equipment={eq}
                    columns={2}
                    dense
                    omitEmpty
                    forReport
                    excludeKeys={REPORT_EQUIPMENT_EXCLUDE}
                  />
                </li>
              ))}
            </ul>
            <p className="mt-3 text-sm font-bold text-gray-900">
              Total: {equipmentList.length} équipement(s)
            </p>
          </section>
        )}

        {maintenanceList.length > 0 && (
          <section className="break-inside-avoid">
            <h3 className="text-base font-bold text-red-600 mb-3">Maintenances</h3>
            <ul className="space-y-4">
              {maintenanceList.map((raw, index) => {
                const m = normalizeMaintenanceRecord(raw);
                return (
                  <li key={m.id ?? index} className="break-inside-avoid">
                    <p className="text-sm font-bold text-gray-900">
                      {index + 1}. {renderCell(m.serialNumber)}
                    </p>
                    <div className="mt-1 space-y-0.5">
                      {fieldLine('Type', maintenanceTypeLabel(m.type))}
                      {fieldLine('Date prévue', formatDate(m.scheduledDate, 'dd/MM/yyyy'))}
                      {fieldLine('Date réalisée', formatDate(m.completedDate, 'dd/MM/yyyy'))}
                      {fieldLine('Statut', maintenanceStatusLabel(m.status))}
                      {fieldLine('Technicien', cellText(m.technicianName))}
                      {m.notes ? fieldLine('Notes', m.notes) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
            <p className="mt-3 text-sm font-bold text-gray-900">
              Total: {maintenanceList.length} maintenance(s)
            </p>
          </section>
        )}

        {showExpiring && (
          <section className="break-inside-avoid">
            <h3 className="text-base font-bold text-red-600 mb-3">Équipements Expirant Bientôt</h3>
            {expiringList.length === 0 ? (
              <p className="text-sm text-gray-700">
                Aucun équipement expirant dans les 10 prochains jours.
              </p>
            ) : (
              <>
                <ul className="space-y-4">
                  {expiringList.map((eq, index) => (
                    <li key={eq.id ?? index} className="break-inside-avoid border-b border-gray-100 pb-4">
                      <p className="text-sm font-bold text-gray-900 mb-2">
                        {index + 1}. {renderCell(normalizeEquipmentRecord(eq).serialNumber)}
                      </p>
                      <EquipmentDetailSummary
                    equipment={eq}
                    columns={2}
                    dense
                    omitEmpty
                    forReport
                    excludeKeys={REPORT_EQUIPMENT_EXCLUDE}
                  />
                    </li>
                  ))}
                </ul>
                <p className="mt-3 text-sm font-bold text-gray-900">
                  Total: {expiringList.length} équipement(s) expirant bientôt
                </p>
              </>
            )}
          </section>
        )}
      </div>

      <footer className="px-6 py-4 text-center text-xs text-gray-500 border-t border-gray-200 print:fixed print:bottom-0 print:left-0 print:right-0">
        © 2025 PETROFEU ÉQUIPEMENTS - Tous droits réservés
      </footer>
    </article>
  );
};

export default SimpleReportDocument;
